import { useQuery } from '@apollo/client';
import { DegreeAvailabilityStatus } from '@coursera-web/degrees';
import { useLocalizedContent } from '@coursera-web/internationalization-lib';
import { useLayer } from '@coursera-web/statsig';
import * as Sentry from '@sentry/react';
import type { Maybe } from '__generated__/graphql-types';
import type { DegreeWebinar_DegreeWebinar as DegreeWebinar } from '__generated__/graphql-types-do-not-use';
import { snakeCase } from 'lodash';

import { useLocation } from 'js/lib/useRouter';

import type {
  GetContentfulSeoDataQuery,
  GetContentfulSeoDataQueryVariables,
  InfoModuleItemFragment,
  PremiumHubAboveTheFoldFieldsFragment,
  PremiumHubBelowTheFoldFieldsFragment,
} from 'bundles/premium-hub/__generated__/contentfulTypes';
import type { DomainIntroModuleProps } from 'bundles/premium-hub/components/degrees/redesign/DomainIntroModule';
import { useDomains } from 'bundles/premium-hub/components/degrees/useDomains';
import type { HubModuleValue, PremiumHubPageType } from 'bundles/premium-hub/constants';
import {
  CATEGORY_TIERS,
  CONTENTFUL_HUB_PAGE_TYPES,
  DEFAULT_MODULE_ORDER,
  DEGREE_HUB_SLUG_TO_PRODUCT_VARIANT,
  HUB_MODULES,
  HubPages,
  LOCALIZED_PAGE_CONTENT_FIELDS,
  PREMIUM_HUB_MODULE_ORDER,
  ProductVariant,
} from 'bundles/premium-hub/constants';
import type { IndustryRelevanceContent } from 'bundles/premium-hub/data/industryRelevanceModuleContent';
import {
  getIndustryRelevanceContent,
  industryRelevanceContent,
} from 'bundles/premium-hub/data/industryRelevanceModuleContent';
import { useCareerRolesByIds } from 'bundles/premium-hub/hooks/useCareerRolesByIds';
import { useDegreesByIds } from 'bundles/premium-hub/hooks/useDegreesByIds';
import useWebinarQuery from 'bundles/premium-hub/hooks/useWebinarQuery';
import GetAllDegreeCategories from 'bundles/premium-hub/queries/GetAllDegreeCategoriesQuery.graphql';
import GetDegreesByFilter from 'bundles/premium-hub/queries/GetDegreesByFilter.graphql';
import GetDegreesById from 'bundles/premium-hub/queries/GetDegreesById.graphql';
import GetDegreesByMarketingType from 'bundles/premium-hub/queries/GetDegreesByMarketingType.graphql';
import type { DegreeCategoryFields_Dedupe1Fragment } from 'bundles/premium-hub/queries/__generated__/DegreeCategoryFieldsFragment';
import type { GetAllDegreeCategories_Dedupe1Query } from 'bundles/premium-hub/queries/__generated__/GetAllDegreeCategoriesQuery';
import type {
  PremiumHubProductsByFilterPaginated_Dedupe1QueryVariables as PpcQueryVariables,
  PremiumHubProductsByFilterPaginated_Dedupe1Query,
} from 'bundles/premium-hub/queries/__generated__/GetDegreesByFilter';
import type {
  PremiumHubGetDegreesById_Dedupe1Query,
  PremiumHubGetDegreesById_Dedupe1QueryVariables,
} from 'bundles/premium-hub/queries/__generated__/GetDegreesById';
import type {
  GetDegreesByMarketingType_Dedupe1Query,
  GetDegreesByMarketingType_Dedupe1QueryVariables,
} from 'bundles/premium-hub/queries/__generated__/GetDegreesByMarketingType';
import type { PremiumProductFields_Dedupe1Fragment } from 'bundles/premium-hub/queries/__generated__/PremiumProductFieldsFragment';
import type { getAllDomains_DomainsV1Resource_domains_elements as Domains } from 'bundles/premium-hub/queries/__generated__/getAllDomains';
import type {
  DegreeCategoryBySlug_Dedupe1Query,
  DegreeCategoryBySlug_Dedupe1QueryVariables,
} from 'bundles/premium-hub/queries/__generated__/getDegreeCategoryBySlug';
import {
  PremiumHubAboveTheFoldFields,
  PremiumHubBelowTheFoldFields,
  getContentfulSeoData,
} from 'bundles/premium-hub/queries/contentfulQueries';
import getDegreeCategoryBySlug from 'bundles/premium-hub/queries/getDegreeCategoryBySlug.graphql';
import type {
  CareerRolesModuleData,
  CategoryData,
  ContentfulVflpConfigProps,
  DegreeContentfulMetadata,
  PremimumHubPageContent,
  PremiumProductWithMetadata,
} from 'bundles/premium-hub/types';
import { AVAILABILITY_STATUS } from 'bundles/premium-hub/types';
import { filterNullOrUndefined, getOverviewListTitle, mapToDegreeContentfulSlug } from 'bundles/premium-hub/utils';
import { useDegreeAtAGlanceFromCms } from 'bundles/premium-hub/utils/contentfulIntegration';
import {
  convertDegreeByMarketingTypeToPPCFields,
  convertDegreeCategoryFieldsToPPCDegreeCategory,
  convertDomainIntroModuleFieldsToProps,
  convertPpcPremiumProductToPremiumProductProps,
  normalizeCareerRolesModuleData,
} from 'bundles/premium-hub/utils/dataTransformationUtils';
import { OPEN_CONTENT_AVAILABLE_ID } from 'bundles/premium-hub/utils/degreeFilterBuckets';
import { sortIndustryRelevanceModules } from 'bundles/premium-hub/utils/sortIndustryRelevanceModules';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';

export type ValuesFromPremiumHubPageData = {
  premiumProductList?: PremiumProductFields_Dedupe1Fragment[] | undefined;
  degreeCategories?: DegreeCategoryFields_Dedupe1Fragment[] | null;
  pageContent?: PremimumHubPageContent;
  useSimplifiedHeader?: boolean;
  domains?: Domains[];
  currentEducationAttainment?: number | null;
  storeEducationAttainment?: (educationalAttainment: number) => void;
  atAGlanceData?: DegreeContentfulMetadata[];
  degreeListWithMetadata: PremiumProductWithMetadata[] | undefined;
  isPreview: boolean;
  isInternationalSubdomain: boolean;
  i18nSupportedLocales?: Maybe<Maybe<string>[]>;
  moduleOrder: HubModuleValue[];
  categoryData: CategoryData | undefined;
  informationSectionContent?: InfoModuleItemFragment | null;
  webinars: {
    pastWebinars: DegreeWebinar[] | undefined;
    upcomingWebinars: DegreeWebinar[] | undefined;
  };
  industryRelevance: IndustryRelevanceContent | IndustryRelevanceContent[] | undefined;
  careerRolesModuleData?: CareerRolesModuleData | null;
  domainIntroModuleData?: DomainIntroModuleProps | null;
  loading: boolean;
  experimentValues: {
    redesign2026Variant: string;
    eoiVariant: string;
    degreeListRefresh: boolean;
  };
};

const getModuleOrder = (moduleOrderFromContentful: string, pageType: PremiumHubPageType): HubModuleValue[] => {
  if (!(pageType in PREMIUM_HUB_MODULE_ORDER)) return [];
  const orders = PREMIUM_HUB_MODULE_ORDER[pageType];
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (moduleOrderFromContentful in orders) return orders[moduleOrderFromContentful];
  const orderEntries = Object.values(orders);
  if (!orderEntries.length) return DEFAULT_MODULE_ORDER;
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return orderEntries[0];
};

const getCategoriesForQuery = (slug: string): string[] | null => {
  switch (slug) {
    case 'public-health':
      return ['HEALTHCARE'];
    case 'maestria-en-linea-latinoamerica':
      return ['TOP_LATAM'];
    default:
      return [snakeCase(slug).toUpperCase()];
  }
};

/**
 * Automatically determines the variables for a query to the premium product collections based on the page type and slug
 * @param slug
 * @param pageType
 * @returns
 */
const getPpcQueryVariables = (
  slug: string,
  pageType: PremiumHubPageType,
  vflpConfig: Maybe<ContentfulVflpConfigProps>
): PpcQueryVariables => {
  const defaultVars = {
    catalogAvailabilityStatus: DegreeAvailabilityStatus.LIVE,
    productVariants: [ProductVariant.BachelorsDegree, ProductVariant.MastersDegree],
  };

  if (pageType === HubPages.DegreesCategoryHubPage) {
    return {
      ...defaultVars,
      productVariants: [...defaultVars.productVariants, ProductVariant.PostgraduateDiploma],
      categories: getCategoriesForQuery(slug),
    };
  } else if (pageType === HubPages.ProductVariantHubPage) {
    if (slug in DEGREE_HUB_SLUG_TO_PRODUCT_VARIANT) {
      return {
        ...defaultVars,
        productVariants: DEGREE_HUB_SLUG_TO_PRODUCT_VARIANT[slug as keyof typeof DEGREE_HUB_SLUG_TO_PRODUCT_VARIANT],
      };
    }
  } else if (pageType === HubPages.LandingPage) {
    if (!vflpConfig?.ppcFilters) return {};
    const { categories, productVariants } = vflpConfig.ppcFilters;
    return {
      ...defaultVars,
      productVariants,
      categories,
    };
  }

  return defaultVars;
};

const getOverviewPageQueryConfigs = (
  slug: string,
  pageType: PremiumHubPageType,
  vflpConfig: Maybe<ContentfulVflpConfigProps>
): {
  skipPpcQuery: boolean;
  ppcQueryVariables: PpcQueryVariables;
  skipByMarketingTypeQuery: boolean;
  byMarketingTypeQueryVariables: GetDegreesByMarketingType_Dedupe1QueryVariables;
  skipQueryByIds: boolean;
  queryByIdsVariables: {
    ids: string[];
  };
} => {
  const skipPpcQuery = pageType === HubPages.LandingPage && !vflpConfig?.ppcFilters;
  const ppcQueryVariables = skipPpcQuery ? {} : getPpcQueryVariables(slug, pageType, vflpConfig);

  const skipByMarketingTypeQuery =
    pageType !== HubPages.LandingPage || !skipPpcQuery || !vflpConfig?.byMarketingTypeFilter;
  const byMarketingTypeQueryVariables = {
    marketingType: vflpConfig?.byMarketingTypeFilter ?? ('PATHWAY' as const),
  };

  const skipQueryByIds =
    pageType !== HubPages.LandingPage || !skipPpcQuery || !skipByMarketingTypeQuery || !vflpConfig?.customIds;
  const queryByIdsVariables = {
    ids: vflpConfig?.customIds?.map((originalId) => originalId.replace('degree', 'base')) ?? [],
  };

  return {
    skipPpcQuery,
    ppcQueryVariables,
    skipByMarketingTypeQuery,
    byMarketingTypeQueryVariables,
    skipQueryByIds,
    queryByIdsVariables,
  };
};

const convertSlugToContentfulSlug = (slug: string, pageType: PremiumHubPageType) =>
  slug === 'degrees' && pageType === HubPages.MainHubPage ? 'degrees_hub' : slug;

const getCategoryData = (
  pageType: PremiumHubPageType,
  slug: string,
  degreeCategoryData: DegreeCategoryBySlug_Dedupe1Query | undefined
): CategoryData | undefined => {
  if (pageType === HubPages.DegreesCategoryHubPage && degreeCategoryData?.DegreeCategoryQueries?.getBySlug) {
    const { name, slug: categorySlug, id } = degreeCategoryData?.DegreeCategoryQueries?.getBySlug;
    return { name, slug: categorySlug, id };
  }

  if (pageType === HubPages.ProductVariantHubPage) {
    const productVariant = DEGREE_HUB_SLUG_TO_PRODUCT_VARIANT[slug as keyof typeof DEGREE_HUB_SLUG_TO_PRODUCT_VARIANT];
    return {
      name: getOverviewListTitle(productVariant),
      slug,
      id: slug,
    };
  }

  if (pageType === HubPages.MainHubPage) {
    return {
      name: '',
      slug: 'degrees',
      id: 'degrees',
    };
  }

  if (pageType === HubPages.LandingPage) {
    return {
      name: '',
      slug,
      id: 'VALUE_FIRST_LANDING_PAGE',
    };
  }

  return undefined;
};

export const getHubPageSlugFromPathname = (pathname: string) => {
  const urlParts = pathname.split('/');
  if (urlParts[urlParts.length - 1] === '') {
    urlParts.pop();
  }
  return urlParts[urlParts.length - 1];
};

const usePremiumHubPageData = (pageType: PremiumHubPageType): ValuesFromPremiumHubPageData | null => {
  const location = useLocation();
  const slug = getHubPageSlugFromPathname(location.pathname);
  const atAGlanceData = useDegreeAtAGlanceFromCms();
  const { domains } = useDomains();
  const degreeHubsLayer = useLayer('degree-hubs');
  const eoiLayer = useLayer('degree-eoi');
  const experimentValues = {
    redesign2026Variant: degreeHubsLayer.get('redesign-2026-variant', 'control'),
    eoiVariant: eoiLayer.get('eoi-variant', 'control'),
    degreeListRefresh: degreeHubsLayer.get('degree-list-refresh', 'control') === 'test',
  };

  const isPreview = location.query.preview ? Number(location.query.preview) === 1 : false;

  // page content via localization app
  const { data: pageContentAboveTheFold, loading: pageContentAboveTheFoldLoading } =
    useLocalizedContent<PremiumHubAboveTheFoldFieldsFragment>({
      isPreview,
      contentType: LOCALIZED_PAGE_CONTENT_FIELDS.contentType,
      fragmentDoc: PremiumHubAboveTheFoldFields,
      fragmentFieldsName: LOCALIZED_PAGE_CONTENT_FIELDS.fragmentFieldsAboveTheFoldName,
      filters: {
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        slug: convertSlugToContentfulSlug(slug, pageType),
        pageType: CONTENTFUL_HUB_PAGE_TYPES[pageType],
      },
      onError: (error) =>
        Sentry.captureException(error, {
          extra: {
            message: 'Error fetching premium hub above the fold page content',
          },
        }),
    });
  const { data: pageContentBelowTheFold } = useLocalizedContent<PremiumHubBelowTheFoldFieldsFragment>({
    isPreview,
    contentType: LOCALIZED_PAGE_CONTENT_FIELDS.contentType,
    fragmentDoc: PremiumHubBelowTheFoldFields,
    fragmentFieldsName: LOCALIZED_PAGE_CONTENT_FIELDS.fragmentFieldsBelowTheFoldName,
    filters: {
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      slug: convertSlugToContentfulSlug(slug, pageType),
      pageType: CONTENTFUL_HUB_PAGE_TYPES[pageType],
    },
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching premium hub below the fold page content',
        },
      }),
  });

  const pageContent: PremimumHubPageContent | undefined = pageContentAboveTheFold
    ? { ...pageContentAboveTheFold, ...pageContentBelowTheFold }
    : undefined;
  const moduleOrder = getModuleOrder(pageContent?.moduleOrder ?? '', pageType);
  const informationSectionContent = pageContent?.visualConsistency?.infoModule;
  const useSimplifiedHeader = pageContent?.useSimplifiedHeader ?? false;

  // degree list
  const vflpConfig = pageContent?.vflpConfiguration;
  const {
    byMarketingTypeQueryVariables,
    ppcQueryVariables,
    skipByMarketingTypeQuery,
    skipPpcQuery,
    queryByIdsVariables,
    skipQueryByIds,
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  } = getOverviewPageQueryConfigs(slug, pageType, vflpConfig);
  const { data: premiumProductCollectionData } = useQuery<
    PremiumHubProductsByFilterPaginated_Dedupe1Query,
    PpcQueryVariables
  >(GetDegreesByFilter, {
    variables: {
      ...ppcQueryVariables,
      page: 1,
      pageSize: 100,
    },
    context: { clientName: 'gatewayGql' },
    skip: skipPpcQuery,
    errorPolicy: 'all',
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching premium hub products by filter',
        },
      }),
  });
  const { data: byIdsData, loading: byIdsLoading } = useQuery<
    PremiumHubGetDegreesById_Dedupe1Query,
    PremiumHubGetDegreesById_Dedupe1QueryVariables
  >(GetDegreesById, {
    variables: {
      ...queryByIdsVariables,
    },
    context: { clientName: 'gatewayGql' },
    skip: skipQueryByIds,
    errorPolicy: 'all',
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching premium hub products by IDs',
        },
      }),
  });
  const {
    data: premiumProductCollectionDataByMarketingType,
    loading: premiumProductCollectionDataByMarketingTypeLoading,
  } = useQuery<GetDegreesByMarketingType_Dedupe1Query, GetDegreesByMarketingType_Dedupe1QueryVariables>(
    GetDegreesByMarketingType,
    {
      variables: byMarketingTypeQueryVariables,
      context: { clientName: 'gatewayGql' },
      skip: skipByMarketingTypeQuery,
      errorPolicy: 'all',
      onError: (error) =>
        Sentry.captureException(error, {
          extra: {
            message: 'Error fetching premium hub products by marketing type',
          },
        }),
    }
  );

  // Query degrees offer open content
  const { data: degreesOfferOpenContentData, loading: degreesOfferOpenContentDataLoading } = useQuery<
    GetDegreesByMarketingType_Dedupe1Query,
    GetDegreesByMarketingType_Dedupe1QueryVariables
  >(GetDegreesByMarketingType, {
    variables: {
      marketingType: 'PATHWAY',
    },
    context: { clientName: 'gatewayGql' },
    skip: !experimentValues.degreeListRefresh,
    errorPolicy: 'all',
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching premium hub degrees with open content',
        },
      }),
  });

  const degreesOfferOpenContentSet = new Set(
    degreesOfferOpenContentData?.PremiumProductCollections?.queryByMarketingType
      ?.filter(filterNullOrUndefined)
      ?.map((degree) => degree.slug)
  );

  let premiumProductList: PremiumProductFields_Dedupe1Fragment[] | undefined;

  if (premiumProductCollectionData) {
    premiumProductList =
      premiumProductCollectionData?.PremiumProductCollections?.queryCollectionPaginated?.learningProducts?.filter(
        filterNullOrUndefined
      );
  } else if (byIdsData) {
    premiumProductList = byIdsData.PremiumProductCollections?.multiGetDegrees?.filter(filterNullOrUndefined);
  } else if (premiumProductCollectionDataByMarketingType) {
    premiumProductList = premiumProductCollectionDataByMarketingType?.PremiumProductCollections?.queryByMarketingType
      ?.filter(filterNullOrUndefined)
      .map(convertDegreeByMarketingTypeToPPCFields);
  }

  // degree categories
  const { data: degreeCategoriesData, loading: degreeCategoriesLoading } = useQuery<
    GetAllDegreeCategories_Dedupe1Query,
    {}
  >(GetAllDegreeCategories, {
    variables: {
      filter: {
        orderBy: 'DRE',
        tiers: [CATEGORY_TIERS.one, CATEGORY_TIERS.two],
        degreeCountSpecs: {
          catalogAvailabilityStatuses: [AVAILABILITY_STATUS.LIVE],
        },
      },
    },
    context: {
      clientName: 'gatewayGql',
    },
    ssr: true,
    errorPolicy: 'all',
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching all degree categories',
        },
      }),
  });
  const degreeCategories = degreeCategoriesData?.DegreeCategoryQueries?.getAll;

  // current category
  const { data: degreeCategoryData, loading: degreeCategoryLoading } = useQuery<
    DegreeCategoryBySlug_Dedupe1Query,
    DegreeCategoryBySlug_Dedupe1QueryVariables
  >(getDegreeCategoryBySlug, {
    skip: pageType !== HubPages.DegreesCategoryHubPage,
    // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    variables: { slug },
    context: { clientName: 'gatewayGql' },
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching degree categories by slug',
        },
      }),
  });

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const categoryData: CategoryData | undefined = getCategoryData(pageType, slug, degreeCategoryData);

  // SEO data
  const isInternationalSubdomain = getSubdomainDetails(location)?.isInternationalSubdomain;
  const { data: contentfulSeoData, loading: contentfulSeoLoading } = useQuery<
    GetContentfulSeoDataQuery,
    GetContentfulSeoDataQueryVariables
  >(getContentfulSeoData, {
    variables: {
      pageLocation: '/degrees',
      preview: isPreview,
    },
    context: { clientName: isPreview ? 'contentfulPreviewGql' : 'contentfulGql' },
    errorPolicy: 'all',
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching contentful SEO data',
        },
      }),
  });

  const i18nSupportedLocales = contentfulSeoData?.seoDataCollection?.items?.[0]?.i18NSupportedLocales;

  // combine degrees with at-a-glance data
  const degreeListWithMetadata: PremiumProductWithMetadata[] | undefined = premiumProductList?.map((degree) => {
    const aagItem = atAGlanceData?.find((item) => item?.slug === mapToDegreeContentfulSlug(degree?.slug ?? '')) ?? {};
    return {
      ...aagItem,
      ...convertPpcPremiumProductToPremiumProductProps(degree),
      openContentAvailability: degreesOfferOpenContentSet.has(degree.slug) ? OPEN_CONTENT_AVAILABLE_ID : null,
    };
  });

  // webinars
  const {
    upcomingWebinars,
    pastWebinars,
    loading: webinarsLoading,
  } = useWebinarQuery(pageContent?.pageId || '', moduleOrder.includes(HUB_MODULES.webinars));

  // industry relevance content
  /**
   * TODO: Remove hard-coded industry relevance and replace with data from Contentful
   */
  const industryRelevanceModules = Object.values(industryRelevanceContent());
  const orderedIndustryRelevanceModules = sortIndustryRelevanceModules(
    industryRelevanceModules,
    degreeCategories?.map(convertDegreeCategoryFieldsToPPCDegreeCategory) ?? []
  );
  const industryRelevance =
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    pageType === HubPages.MainHubPage ? orderedIndustryRelevanceModules : getIndustryRelevanceContent(slug);

  // career roles module
  const careerRolesModule = pageContent?.redesign2026Modules?.careerRolesModule;
  const { data: careerRolesData, loading: careerRolesDataLoading } = useCareerRolesByIds(
    careerRolesModule?.rolesCollection?.items?.flatMap((item) => {
      return [item?.roleId, item?.parentRoleId].filter(Boolean);
    }) ?? [],
    pageType !== HubPages.DegreesCategoryHubPage || experimentValues.redesign2026Variant === 'control',
    true
  );
  const { data: careerRolesRelatedDegreesData, loading: careerRolesRelatedDegreesDataLoading } = useDegreesByIds(
    careerRolesModule?.rolesCollection?.items?.map((item) => item?.relatedDegreeId)?.filter(Boolean) ?? [],
    pageType !== HubPages.DegreesCategoryHubPage || experimentValues.redesign2026Variant === 'control'
  );

  const careerRolesModuleData = normalizeCareerRolesModuleData(
    careerRolesModule,
    careerRolesData,
    careerRolesRelatedDegreesData
  );

  // domain intro module
  const domainIntroductionModule = pageContent?.redesign2026Modules?.domainIntroductionModule;
  const domainIntroModuleData = domainIntroductionModule
    ? convertDomainIntroModuleFieldsToProps(domainIntroductionModule)
    : null;

  const renderBlockingRequestsLoading =
    degreeCategoriesLoading ||
    degreeCategoryLoading ||
    pageContentAboveTheFoldLoading ||
    premiumProductCollectionDataByMarketingTypeLoading ||
    webinarsLoading ||
    contentfulSeoLoading ||
    byIdsLoading ||
    careerRolesDataLoading ||
    careerRolesRelatedDegreesDataLoading ||
    degreesOfferOpenContentDataLoading;

  if (renderBlockingRequestsLoading) {
    // Don't return anything until all the required data is loaded, this limits the amount of re-renders
    return null;
  }

  return {
    premiumProductList,
    useSimplifiedHeader,
    degreeCategories,
    categoryData,
    pageContent,
    domains,
    atAGlanceData,
    isPreview,
    isInternationalSubdomain,
    i18nSupportedLocales,
    moduleOrder,
    degreeListWithMetadata,
    informationSectionContent,
    webinars: { pastWebinars, upcomingWebinars },
    industryRelevance,
    domainIntroModuleData,
    careerRolesModuleData,
    loading: renderBlockingRequestsLoading, // TODO: this shouldn't be needed
    experimentValues,
  };
};

export default usePremiumHubPageData;
